/**
 * Shared Font Loader Utility
 * This class manages font loading across both web and admin applications
 * Uses only browser APIs, no framework dependencies
 */
export class FontLoader {
  private static loadedFonts = new Set<string>();

  /**
   * Load a custom font from a URL
   * @param fontName The name to register the font as
   * @param fontUrl The URL to the font file
   * @returns Promise that resolves when font is loaded
   */
  public static async loadFont(fontName: string, fontUrl: string): Promise<void> {
    if (this.loadedFonts.has(fontName)) {
      return; // Already loaded
    }

    try {
      const font = new FontFace(fontName, `url(${fontUrl})`);
      await font.load();
      document.fonts.add(font);
      this.loadedFonts.add(fontName);
      console.log(`${fontName} font loaded successfully`);
    } catch (error) {
      console.warn(`Failed to load ${fontName} font, falling back to default:`, error);
      // Don't throw error - we'll fall back to default font
    }
  }

  /**
   * Load Gobold font specifically
   * @param fontPath Optional custom path to the font file (defaults to /fonts/gobold.otf)
   */
  public static async loadGoboldFont(fontPath: string = '/fonts/gobold.otf'): Promise<void> {
    return this.loadFont('Gobold', fontPath);
  }

  /**
   * Check if a font family is available
   * @param fontFamily The font family name to check
   * @returns boolean indicating if the font is available
   */
  public static isFontAvailable(fontFamily: string): boolean {
    return document.fonts.check(`16px ${fontFamily}`);
  }

  /**
   * Get the font family string with fallbacks
   * @param primaryFont The primary font to use
   * @param fallbacks Array of fallback fonts (defaults to Arial, sans-serif)
   * @returns Font family string for CSS
   */
  public static getFontFamily(
    primaryFont: string = 'Gobold',
    fallbacks: string[] = ['Arial', 'sans-serif']
  ): string {
    return [primaryFont, ...fallbacks].join(', ');
  }
}